import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { ApiNewsService } from '../../../../../../../services/news/api/api-news.service';
import { AlertsService } from '../../../../../../../services/alerts.service';
import { LanguagesService } from '../../../../../../../services/languages/languages.service';
import { NewsPredefined } from '../../../../../../../models/news/news-predefined.model';
import { NewsPredefinedTranslation } from '../../../../../../../models/news/news-predefined-translation.model';
import { NewsReward } from '../../../../../../../models/news/news-reward.model';
import { UserCurrencyType } from '../../../../../../../enums/users/user-currency-type.enum';

interface RewardForm {
  currencyType: number;
  currencyQuantity: number;
  currencyTicketCost: number;
}

@Component({
  selector: 'app-news-user-add-modal',
  templateUrl: './news-user-add-modal.component.html',
  standalone: true,
  imports: [ModalModule, CommonModule, FormsModule]
})
export class NewsUserAddModalComponent {

  @ViewChild('modal') modal!: ModalDirective;

  isLoading: boolean = false;
  private userId: number = 0;
  private userLocale: string = '';
  selectedPredefinedId: number = 0;
  title: string = '';
  summary: string = '';
  content: string = '';
  isRate: boolean = false;
  rewards: RewardForm[] = [];
  predefinedList: NewsPredefined[] = [];
  private filledFromPredefined: boolean = false;

  readonly currencyTypes = Object.entries(UserCurrencyType)
    .filter(([, v]) => typeof v === 'number') as [string, number][];

  private callbackSaved: (() => void) | undefined;

  constructor(
    private apiNewsService: ApiNewsService,
    private alertsService: AlertsService,
    private languagesService: LanguagesService
  ) {}

  show(userId: number, userLocale: string, callbackSaved?: () => void): void {
    this.callbackSaved = callbackSaved;
    this.userId = userId;
    this.userLocale = userLocale;
    this.selectedPredefinedId = 0;
    this.title = '';
    this.summary = '';
    this.content = '';
    this.isRate = false;
    this.rewards = [];
    this.isLoading = false;
    this.apiNewsService.getNewsPredefined(1, 1000).then(
      response => { this.predefinedList = response.results; }
    );
    this.modal.show();
  }

  onPredefinedChange(): void {
    if (this.selectedPredefinedId === 0) {
      this.filledFromPredefined = false;
      return;
    }
    const predefined = this.predefinedList.find(p => p.newsPredefinedId === this.selectedPredefinedId);
    if (predefined && predefined.translations.length > 0) {
      const tr = this.pickTranslationForLocale(predefined.translations);
      this.title = tr.title;
      this.summary = tr.summary;
      this.content = tr.content;
      this.filledFromPredefined = true;
    }
  }

  private pickTranslationForLocale(translations: NewsPredefinedTranslation[]): NewsPredefinedTranslation {
    const locale = (this.userLocale ?? '').toLowerCase();
    if (locale) {
      const localePrefix = locale.split(/[_-]/)[0];
      const match = this.languagesService.languages.find(l => {
        const iso = (l.languageIso ?? '').toLowerCase();
        return iso === locale || iso.split(/[_-]/)[0] === localePrefix;
      });
      if (match) {
        const tr = translations.find(t => t.languageId === match.languageId);
        if (tr) return tr;
      }
    }
    return translations[0];
  }

  onFieldChanged(): void {
    if (this.filledFromPredefined) {
      this.selectedPredefinedId = 0;
      this.filledFromPredefined = false;
    }
  }

  addRewardRow(): void {
    this.rewards.push({ currencyType: 0, currencyQuantity: 0, currencyTicketCost: 0 });
  }

  removeRewardRow(index: number): void {
    this.rewards.splice(index, 1);
  }

  save(): void {
    this.isLoading = true;
    const newsPredefinedId = this.selectedPredefinedId || null;
    const rewardsPayload: NewsReward[] = this.rewards.map(r => {
      const reward = new NewsReward();
      reward.currencyType = r.currencyType;
      reward.currencyQuantity = r.currencyQuantity;
      reward.currencyTicketCost = r.currencyTicketCost;
      return reward;
    });

    this.apiNewsService.addNewsUser(this.userId, newsPredefinedId, this.title, this.summary, this.content, this.isRate, rewardsPayload).then(
      () => {
        this.isLoading = false;
        this.modal.hide();
        this.alertsService.displayAlertSuccess('Message added !');
        if (this.callbackSaved) { this.callbackSaved(); }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error adding message : ' + error);
      }
    );
  }
}
